import { useState } from "react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { Folder, FolderOpen, FolderPlus, Trash2, Files } from "lucide-react";
import type { KBFolder } from "@/lib/knowledge-base-service";

interface FolderTreeProps {
  folders: KBFolder[];
  selectedFolderId: string | null | undefined;
  onSelectFolder: (folderId: string | null | undefined) => void;
  onCreateFolder: () => void;
  onDeleteFolder: (folder: KBFolder) => void;
}

interface FolderItemProps {
  folder: KBFolder;
  isSelected: boolean;
  onSelect: () => void;
  onDelete: () => void;
}

function FolderItem({ folder, isSelected, onSelect, onDelete }: FolderItemProps) {
  const [hovered, setHovered] = useState(false);

  return (
    <div
      className={cn(
        "group flex items-center gap-2 rounded-lg px-3 py-2 text-sm cursor-pointer transition-colors",
        isSelected
          ? "bg-primary/10 text-primary font-medium"
          : "text-muted-foreground hover:bg-muted hover:text-foreground"
      )}
      onClick={onSelect}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      {isSelected ? (
        <FolderOpen className="h-4 w-4 shrink-0" />
      ) : (
        <Folder className="h-4 w-4 shrink-0" />
      )}
      <span className="flex-1 truncate">{folder.name}</span>
      {hovered && (
        <button
          className="h-5 w-5 flex items-center justify-center rounded opacity-0 group-hover:opacity-100 hover:text-destructive transition-opacity"
          onClick={e => {
            e.stopPropagation();
            onDelete();
          }}
        >
          <Trash2 className="h-3 w-3" />
        </button>
      )}
    </div>
  );
}

export function FolderTree({
  folders,
  selectedFolderId,
  onSelectFolder,
  onCreateFolder,
  onDeleteFolder,
}: FolderTreeProps) {
  return (
    <div className="flex flex-col gap-0.5">
      {/* All documents */}
      <div
        className={cn(
          "flex items-center gap-2 rounded-lg px-3 py-2 text-sm cursor-pointer transition-colors",
          selectedFolderId === undefined
            ? "bg-primary/10 text-primary font-medium"
            : "text-muted-foreground hover:bg-muted hover:text-foreground"
        )}
        onClick={() => onSelectFolder(undefined)}
      >
        <Files className="h-4 w-4 shrink-0" />
        <span className="flex-1">All documents</span>
      </div>

      {/* Folder list */}
      {folders.map(folder => (
        <FolderItem
          key={folder.id}
          folder={folder}
          isSelected={selectedFolderId === folder.id}
          onSelect={() => onSelectFolder(folder.id)}
          onDelete={() => onDeleteFolder(folder)}
        />
      ))}

      {/* Add folder button */}
      <button
        className="flex items-center gap-2 rounded-lg px-3 py-2 text-sm text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
        onClick={onCreateFolder}
      >
        <FolderPlus className="h-4 w-4" />
        <span>New folder</span>
      </button>
    </div>
  );
}
